import {
  AbsoluteFill,
  Audio,
  OffthreadVideo,
  Sequence,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
} from "remotion";

const FPS = 30;

const hexToRgba = (hex, opacityPercent) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "transparent";
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  const a = Math.min(100, Math.max(0, opacityPercent)) / 100;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const KEN_BURNS_EFFECTS = ["zoom-in", "zoom-out", "pan-right", "pan-left"];

function getKenBurnsTransform(effectName, progress) {
  switch (effectName) {
    case "zoom-in":
      return `scale(${1 + progress * 0.15})`;
    case "zoom-out":
      return `scale(${1.15 - progress * 0.15})`;
    case "pan-right":
      return `scale(${1 + progress * 0.12}) translateX(${-progress * 5}%)`;
    case "pan-left":
      return `scale(${1 + progress * 0.12}) translateX(${progress * 5}%)`;
    default:
      return `scale(1)`;
  }
}

function SegmentVisual({ segment, index }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const segmentDuration = segment.endTime - segment.startTime;
  const totalFrames = Math.max(1, Math.round(segmentDuration * fps));
  const progress = Math.min(1, frame / totalFrames);

  const effectName = KEN_BURNS_EFFECTS[index % KEN_BURNS_EFFECTS.length];
  
  // Check if we should show animated video:
  // - For thumbnail images: animation_status === "completed" && animated_video_url exists
  // - For draft segments: animate_enabled && animated_video_url exists
  // - Fallback: just check if animated_video_url exists (for direct API responses)
  const hasAnimatedVideo = segment.animated_video_url && (
    segment.animation_status === "completed" ||
    segment.animate_enabled ||
    (!segment.animation_status && !segment.hasOwnProperty('animate_enabled'))
  );

  if (hasAnimatedVideo) {
    return (
      <AbsoluteFill>
        <OffthreadVideo
          src={segment.animated_video_url}
          muted
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      </AbsoluteFill>
    );
  }

  return (
    <AbsoluteFill style={{ overflow: "hidden" }}>
      <img
        src={segment.image_url}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          transform: getKenBurnsTransform(effectName, progress),
          transformOrigin: "center center",
        }}
      />
    </AbsoluteFill>
  );
}

function CrossfadeTransition({ children, durationInFrames, transitionFrames }) {
  const frame = useCurrentFrame();

  const fadeIn = interpolate(frame, [0, transitionFrames], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const fadeOut = interpolate(
    frame,
    [durationInFrames - transitionFrames, durationInFrames],
    [1, 0],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    }
  );

  const opacity = Math.min(fadeIn, fadeOut);

  return <AbsoluteFill style={{ opacity }}>{children}</AbsoluteFill>;
}

const DEFAULT_SUBTITLE_SETTINGS = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontSize: 48,
  color: "#ffffff",
  position: "bottom-center",
};

const POSITION_STYLES = {
  "top-left":      { justifyContent: "flex-start", alignItems: "flex-start", paddingTop: 40, paddingLeft: 40 },
  "top-center":    { justifyContent: "flex-start", alignItems: "center",     paddingTop: 40 },
  "top-right":     { justifyContent: "flex-start", alignItems: "flex-end",   paddingTop: 40, paddingRight: 40 },
  "center-left":   { justifyContent: "center",     alignItems: "flex-start", paddingLeft: 40 },
  "center-center": { justifyContent: "center",     alignItems: "center" },
  "center-right":  { justifyContent: "center",     alignItems: "flex-end",   paddingRight: 40 },
  "bottom-left":   { justifyContent: "flex-end",   alignItems: "flex-start", paddingBottom: 40, paddingLeft: 40 },
  "bottom-center": { justifyContent: "flex-end",   alignItems: "center",     paddingBottom: 40 },
  "bottom-right":  { justifyContent: "flex-end",   alignItems: "flex-end",   paddingBottom: 40, paddingRight: 40 },
};

function SubtitleOverlay({ subtitles, subtitleSettings = {} }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const currentTime = frame / fps;

  const settings = { ...DEFAULT_SUBTITLE_SETTINGS, ...subtitleSettings };
  const positionStyle = POSITION_STYLES[settings.position] || POSITION_STYLES["bottom-center"];

  const currentSubtitle = subtitles.find(
    (sub) => currentTime >= sub.startTime && currentTime < sub.endTime
  );

  if (!currentSubtitle) return null;

  const bgStyle = settings.backgroundColorEnabled
    ? { backgroundColor: hexToRgba(settings.backgroundColor || "#000000", settings.backgroundColorOpacity ?? 50) }
    : {};

  return (
    <AbsoluteFill style={positionStyle}>
      <div
        style={{
          ...bgStyle,
          color: settings.color,
          padding: "8px 16px",
          borderRadius: 8,
          fontSize: settings.fontSize,
          fontWeight: 600,
          textAlign: "center",
          maxWidth: "90%",
          textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
          fontFamily: settings.fontFamily,
        }}
      >
        {currentSubtitle.text}
      </div>
    </AbsoluteFill>
  );
}

function BrandingWatermark() {
  return (
    <AbsoluteFill
      style={{
        justifyContent: "flex-end",
        alignItems: "flex-start",
        padding: 20,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          color: "rgba(255, 255, 255, 0.7)",
          fontSize: 32,
          fontWeight: 500,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          textShadow: "1px 1px 3px rgba(0,0,0,0.6)",
          letterSpacing: 0.3,
        }}
      >
        Made with PromptToVid.com
      </div>
    </AbsoluteFill>
  );
}

export default function VideoComposition({
  segments = [],
  subtitles = [],
  subtitlesEnabled = false,
  subtitleSettings = {},
  audioUrl = null,
  soundEffectClips = [],
  showWatermark = true,
}) {
  const { fps } = useVideoConfig();
  const TRANSITION_FRAMES = Math.round(0.3 * fps);

  return (
    <AbsoluteFill style={{ backgroundColor: "black" }}>
      {/* Main narration audio (used by Lambda render; browser Player uses a separate <audio> element) */}
      {audioUrl && <Audio src={audioUrl} />}

      {/* Video/Image Segments */}
      {segments.map((segment, index) => {
        const startFrame = Math.round(segment.startTime * fps);
        const endFrame = Math.round(segment.endTime * fps);
        const durationFrames = endFrame - startFrame;

        if (durationFrames <= 0) return null;

        return (
          <Sequence
            key={segment.id}
            from={startFrame}
            durationInFrames={durationFrames}
          >
            <CrossfadeTransition
              durationInFrames={durationFrames}
              transitionFrames={TRANSITION_FRAMES}
            >
              <SegmentVisual segment={segment} index={index} />
            </CrossfadeTransition>
          </Sequence>
        );
      })}

      {/* Sound Effect Clips */}
      {soundEffectClips.map((clip) => {
        const sfxUrl = clip.asset?.audio_url || clip.asset?.preview_url;
        if (!sfxUrl) return null;

        const startFrame = Math.round((clip.start_time || 0) * fps);
        const endFrame = Math.round((clip.end_time || 0) * fps);
        const durationFrames = endFrame - startFrame;

        if (durationFrames <= 0) return null;

        return (
          <Sequence
            key={`sfx-${clip.id}`}
            from={startFrame}
            durationInFrames={durationFrames}
          >
            <Audio
              src={sfxUrl}
              volume={(clip.volume || 50) / 100}
              loop={clip.loop_enabled}
            />
          </Sequence>
        );
      })}

      {/* Subtitle Overlay */}
      {subtitlesEnabled && subtitles.length > 0 && (
        <SubtitleOverlay subtitles={subtitles} subtitleSettings={subtitleSettings} />
      )}

      {/* Branding Watermark */}
      {showWatermark && <BrandingWatermark />}
    </AbsoluteFill>
  );
}

export { FPS };
